import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Loader2, Save, Plus, Trash2, ChevronUp, ChevronDown, ExternalLink, GripVertical,
} from "lucide-react";
import { adminGet, adminPut } from "../../lib/api";

const NavigationEditor = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pages, setPages] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [nav, pgs] = await Promise.all([adminGet("/nav"), adminGet("/pages")]);
        setItems(nav);
        setPages(pgs.filter((p) => p.published));
      } catch (e) {
        toast.error("Failed to load navigation");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const update = (idx, patch) => setItems((s) => s.map((it, i) => (i === idx ? { ...it, ...patch } : it)));

  const move = (idx, dir) => {
    setItems((s) => {
      const arr = [...s];
      const swapWith = idx + dir;
      if (swapWith < 0 || swapWith >= arr.length) return s;
      [arr[idx], arr[swapWith]] = [arr[swapWith], arr[idx]];
      return arr.map((it, i) => ({ ...it, order: i + 1 }));
    });
  };

  const remove = (idx) => setItems((s) => s.filter((_, i) => i !== idx).map((it, i) => ({ ...it, order: i + 1 })));

  const add = () => setItems((s) => [...s, { id: null, label: "New Item", target: "/", external: false, order: s.length + 1 }]);

  const save = async () => {
    for (const it of items) {
      if (!it.label.trim() || !it.target.trim()) return toast.error("Every item needs a label and a target");
    }
    setSaving(true);
    try {
      const saved = await adminPut("/nav", { items });
      setItems(saved);
      toast.success("Navigation saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-[#0A192F]/40">
        <Loader2 className="animate-spin mr-2" size={18} /> Loading...
      </div>
    );
  }

  return (
    <div data-testid="admin-nav-editor" className="space-y-6">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="font-display text-[11px] uppercase tracking-[0.3em] text-[#0A192F]/60">Content</div>
          <h1 className="mt-2 font-display font-black text-4xl text-[#0A192F] tracking-tighter">Navigation</h1>
          <p className="mt-3 text-[#0A192F]/70 max-w-2xl text-sm">
            Reorder, rename, or link menu items. Target can be an internal path (<code className="bg-gray-100 px-1 text-xs">/services</code>, <code className="bg-gray-100 px-1 text-xs">/pages/&lt;slug&gt;</code>) or a full external URL.
          </p>
        </div>
        <button
          onClick={save}
          disabled={saving}
          data-testid="nav-save-btn"
          className="inline-flex items-center gap-2 bg-[#0A192F] text-white px-5 py-3 font-display text-xs uppercase tracking-[0.25em] font-bold hover:bg-[#D4AF37] hover:text-[#0A192F] disabled:opacity-50"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save Navigation
        </button>
      </header>

      <div className="bg-white border border-gray-200">
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <div className="font-display text-xs uppercase tracking-[0.3em] text-[#0A192F]/60 font-semibold">Menu Items</div>
          <button
            onClick={add}
            data-testid="nav-add-item"
            className="inline-flex items-center gap-2 text-xs font-display uppercase tracking-wider font-bold text-[#0A192F] border border-gray-300 px-3 py-1.5 hover:border-[#D4AF37] hover:text-[#D4AF37]"
          >
            <Plus size={12} /> Add Item
          </button>
        </div>
        <ul>
          {items.map((it, idx) => (
            <li key={`${it.id || "new"}-${idx}`} className="border-t border-gray-100 p-4" data-testid={`nav-item-${idx}`}>
              <div className="flex items-start gap-3">
                <div className="text-[#0A192F]/30 pt-3"><GripVertical size={14} /></div>
                <div className="flex-1 grid sm:grid-cols-12 gap-3">
                  <div className="sm:col-span-4">
                    <Label>Label</Label>
                    <input
                      value={it.label}
                      onChange={(e) => update(idx, { label: e.target.value })}
                      data-testid={`nav-label-${idx}`}
                      className="mt-1 w-full border border-gray-300 px-3 py-2 text-sm focus:border-[#D4AF37] focus:outline-none"
                    />
                  </div>
                  <div className="sm:col-span-6">
                    <Label>Target (URL or path)</Label>
                    <div className="mt-1 flex gap-2">
                      <input
                        value={it.target}
                        onChange={(e) => update(idx, { target: e.target.value })}
                        placeholder="/pages/about-us or https://example.com"
                        data-testid={`nav-target-${idx}`}
                        className="flex-1 border border-gray-300 px-3 py-2 text-sm font-mono focus:border-[#D4AF37] focus:outline-none"
                      />
                      {pages.length > 0 && (
                        <select
                          value=""
                          onChange={(e) => {
                            if (e.target.value) update(idx, { target: e.target.value, external: false });
                          }}
                          data-testid={`nav-page-picker-${idx}`}
                          className="border border-gray-300 px-2 py-2 text-xs focus:border-[#D4AF37] focus:outline-none"
                          title="Link to a page"
                        >
                          <option value="">Link to page...</option>
                          {pages.map((p) => (
                            <option key={p.slug} value={`/pages/${p.slug}`}>{p.title}</option>
                          ))}
                        </select>
                      )}
                    </div>
                  </div>
                  <div className="sm:col-span-2">
                    <Label>External</Label>
                    <label className="mt-1 flex items-center gap-2 h-[38px] text-sm">
                      <input
                        type="checkbox"
                        checked={it.external}
                        onChange={(e) => update(idx, { external: e.target.checked })}
                        data-testid={`nav-external-${idx}`}
                        className="w-4 h-4 accent-[#D4AF37]"
                      />
                      <ExternalLink size={12} className="text-[#0A192F]/50" />
                    </label>
                  </div>
                </div>
                <div className="flex flex-col gap-1 pt-4">
                  <IconBtn onClick={() => move(idx, -1)} disabled={idx === 0} testId={`nav-up-${idx}`}>
                    <ChevronUp size={12} />
                  </IconBtn>
                  <IconBtn onClick={() => move(idx, 1)} disabled={idx === items.length - 1} testId={`nav-down-${idx}`}>
                    <ChevronDown size={12} />
                  </IconBtn>
                  <IconBtn onClick={() => remove(idx)} danger testId={`nav-remove-${idx}`}>
                    <Trash2 size={12} />
                  </IconBtn>
                </div>
              </div>
            </li>
          ))}
          {items.length === 0 && (
            <li className="p-8 text-center text-sm text-[#0A192F]/60">
              No menu items. Click <strong>Add Item</strong> above.
            </li>
          )}
        </ul>
      </div>
    </div>
  );
};

const Label = ({ children }) => (
  <span className="font-display text-[10px] uppercase tracking-[0.3em] text-[#0A192F]/60 font-semibold">{children}</span>
);

const IconBtn = ({ children, onClick, disabled, danger, testId }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    data-testid={testId}
    className={`p-1.5 border transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${
      danger ? "border-red-200 text-red-600 hover:bg-red-50" : "border-gray-300 text-[#0A192F] hover:border-[#D4AF37] hover:text-[#D4AF37]"
    }`}
  >
    {children}
  </button>
);

export default NavigationEditor;
